import type { ComponentType } from '@grafikr/components';
import throttle from 'lodash/throttle';
import { disablePageScroll, enablePageScroll, setFillGapMethod } from 'scroll-lock';

import type { Cart } from '@grafikr/shopify-typescript/type/json/Cart';
import transitionEnd from 'utils/transition-end';
import repaint from 'utils/repaint';

interface Config {
  context: 'static' | 'sticky';
}

interface Megamenu {
  element: HTMLElement;
  isActive: boolean;
  link: HTMLElement;
  trigger: HTMLElement;
}

const component: ComponentType = (node, { emitter }) => {
  const cartBtn = node.querySelector<HTMLElement>('[data-cart-btn]');
  const megamenuElements = Array.from(node.querySelectorAll<HTMLElement>('[data-megamenu]'));
  const megamenuTriggerElements = Array.from(
    node.querySelectorAll<HTMLElement>('[data-megamenu-trigger]'),
  );
  const mobileMenuBtn = node.querySelector('[data-mobile-menu-btn]');
  const searchBtn = node.querySelector('[data-search-btn]');

  const config: Config = JSON.parse(node.querySelector('[data-config]').innerHTML);
  const { context } = config;

  const HEADER_SCROLLED_CLASS = 'header--scrolled';
  const HEADER_ACTIVE_CLASS = 'header--active';
  const HEADER_HAS_MEGAMENU_CLASS = 'header--has-megamenu';
  const MEGAMENU_ACTIVE_CLASS = 'megamenu--active';
  const LINK_ACTIVE_CLASS = 'header__link--active';

  let previousY = window.scrollY;

  const initSearchBtn = () => {
    searchBtn.addEventListener('click', () => emitter.emit('live-search:open'));
  };

  const initCartBtn = () => {
    if (cartBtn) {
      cartBtn.addEventListener('click', (event) => {
        event.preventDefault();
        emitter.emit('cart:open');
      });
    }

    emitter.on('cart:updated', (cart: Cart) => {
      if (cartBtn) {
        cartBtn.textContent = theme.strings.header.cart.replace(
          '{{ count }}',
          cart.item_count.toString(),
        );
      }
    });
  };

  const initMobileMenuBtn = () => {
    mobileMenuBtn.addEventListener('click', () => {
      emitter.emit('mobile-menu:open');
    });
  };

  const megamenus: Array<Megamenu> = megamenuElements.map((element) => {
    const { title } = element.dataset;

    const trigger = megamenuTriggerElements.find(
      (triggerElement) => triggerElement.dataset.title === title,
    );

    const link = trigger.querySelector<HTMLElement>('[data-link]');

    return {
      element,
      isActive: false,
      link,
      trigger,
    };
  });

  const closeMegamenu = (megamenu: Megamenu) => {
    node.classList.remove(HEADER_HAS_MEGAMENU_CLASS);
    megamenu.element.classList.remove(MEGAMENU_ACTIVE_CLASS);

    transitionEnd(
      megamenu.element,
      () => {
        if (!megamenu.isActive) {
          megamenu.element.hidden = true;
          megamenu.trigger.ariaExpanded = 'false';
        }
      },
      true,
    );

    megamenu.link.classList.remove(LINK_ACTIVE_CLASS);
    megamenu.isActive = false;

    enablePageScroll();
  };

  const handleClick = (event: MouseEvent) => {
    const trigger = megamenuTriggerElements.find((megamenuTriggerElement) =>
      megamenuTriggerElement.contains(event.target as HTMLElement),
    );

    const clickedOverlay = (event.target as HTMLElement).dataset.megamenu !== undefined;

    if (!trigger || clickedOverlay) {
      megamenus.forEach((megamenu) => {
        closeMegamenu(megamenu);
      });

      document.removeEventListener('click', handleClick);
    }
  };

  const openMegamenu = (megamenu: Megamenu) => {
    megamenus.forEach((otherMegamenu) => {
      closeMegamenu(otherMegamenu);
    });

    node.classList.add(HEADER_HAS_MEGAMENU_CLASS);
    megamenu.element.hidden = false;
    megamenu.trigger.ariaExpanded = 'true';
    repaint(megamenu.element);
    megamenu.element.classList.add(MEGAMENU_ACTIVE_CLASS);
    megamenu.link.classList.add(LINK_ACTIVE_CLASS);

    setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 0);

    megamenu.isActive = true;

    disablePageScroll();
  };

  const toggleMegamenu = (megamenu: Megamenu) => {
    if (megamenu.isActive) {
      closeMegamenu(megamenu);
    } else {
      openMegamenu(megamenu);
    }
  };

  const initMegamenus = () => {
    megamenus.forEach((megamenu) => {
      megamenu.trigger.addEventListener('click', (event) => {
        if (!megamenu.element.contains(event.target as HTMLAnchorElement)) {
          event.preventDefault();
          toggleMegamenu(megamenu);
        }
      });
    });
  };

  const initScrolling = () => {
    // eslint-disable-next-line default-case
    switch (context) {
      case 'static':
        // eslint-disable-next-line no-case-declarations
        const observer = new IntersectionObserver(([entry]) => {
          const event =
            !entry.isIntersecting && entry.boundingClientRect.y < 0 ? 'header:show' : 'header:hide';

          emitter.emit(event);
        });

        observer.observe(node);

        break;

      case 'sticky':
        emitter.on('header:show', () => {
          node.classList.add(HEADER_SCROLLED_CLASS);
        });

        emitter.on('header:hide', () => {
          node.classList.remove(HEADER_SCROLLED_CLASS);
        });

        window.addEventListener(
          'scroll',
          throttle(() => {
            const currentY = window.scrollY;
            const setActive =
              currentY < previousY && node.classList.contains(HEADER_SCROLLED_CLASS);

            node.classList[setActive ? 'add' : 'remove'](HEADER_ACTIVE_CLASS);

            previousY = currentY;
          }, 50),
        );

        break;
    }
  };

  const init = () => {
    initSearchBtn();
    initCartBtn();
    initMobileMenuBtn();
    initMegamenus();
    initScrolling();

    setFillGapMethod('none');
  };

  init();
};

export default component;
